import { Link } from 'react-router-dom';
import { ArrowRight, Lamp, Gift, UtensilsCrossed, Sparkles, BookOpen, Heart, Calendar } from 'lucide-react';
import { SEOHead } from '../components/SEOHead';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { EidCountdown } from '../components/EidCountdown';

const sections = [
  {
    title: 'Ramadan Geschenke',
    desc: 'Die schönsten Geschenkideen für den Fastenmonat und das Fastenbrechen.',
    articles: [
      { slug: 'ramadan-geschenke', title: 'Ramadan Geschenke: Die schönsten Ideen', readTime: '10 Min.' },
      { slug: 'geschenkboxen-eid', title: 'Eid Geschenkboxen: Die schönsten Sets', readTime: '7 Min.' },
      { slug: 'personalisierte-geschenke-eid', title: 'Personalisierte Geschenke zu Eid', readTime: '8 Min.' },
      { slug: 'diy-geschenke-eid', title: 'DIY Geschenke zu Eid selber machen', readTime: '10 Min.' },
    ],
    icon: Gift,
  },
  {
    title: 'Ramadan Rezepte & Essen',
    desc: 'Iftar-Rezepte, Frühstücksideen und die besten Dattelsorten.',
    articles: [
      { slug: 'ramadan-iftar-rezepte', title: 'Ramadan Iftar Rezepte: Traditionell & Modern', readTime: '12 Min.' },
      { slug: 'eid-fruehstueck-ideen', title: 'Eid Frühstück: Das perfekte Festmahl', readTime: '10 Min.' },
      { slug: 'eid-suessigkeiten-selber-machen', title: 'Eid Süßigkeiten selber machen', readTime: '10 Min.' },
      { slug: 'dattelsorten-ramadan', title: 'Die besten Dattelsorten für Ramadan', readTime: '8 Min.' },
      { slug: 'halal-suessigkeiten-deutschland', title: 'Halal Süßigkeiten in Deutschland', readTime: '10 Min.' },
    ],
    icon: UtensilsCrossed,
  },
  {
    title: 'Ramadan Dekoration',
    desc: 'Deko-Ideen, DIY-Anleitungen und stimmungsvolle Beleuchtung.',
    articles: [
      { slug: 'eid-deko-ideen', title: 'Eid Deko Ideen für Zuhause', readTime: '9 Min.' },
      { slug: 'ramadan-lichter-deko', title: 'Ramadan Lichter & Laternen', readTime: '7 Min.' },
      { slug: 'diy-tischdeko-eid', title: 'DIY Tischdeko für das Eid-Festmahl', readTime: '8 Min.' },
      { slug: 'minimalistische-eid-deko', title: 'Minimalistische Eid Dekoration', readTime: '8 Min.' },
      { slug: 'ramadan-kalender-selber-machen', title: 'Ramadan Kalender selber machen', readTime: '10 Min.' },
    ],
    icon: Sparkles,
  },
  {
    title: 'Kinder im Ramadan',
    desc: 'Aktivitäten, Spiele, Bücher und Geschenke für Kinder.',
    articles: [
      { slug: 'ramadan-aktivitaeten-kinder', title: 'Ramadan Aktivitäten für Kinder', readTime: '9 Min.' },
      { slug: '25-eid-geschenke-kinder', title: 'Die 25 besten Eid Geschenke für Kinder', readTime: '15 Min.' },
      { slug: 'islamische-buecher-kinder', title: 'Islamische Kinderbücher: Empfehlungen', readTime: '9 Min.' },
      { slug: 'eid-spiele-quiz', title: 'Eid Spiele und Quiz für die Familie', readTime: '7 Min.' },
      { slug: 'eid-rezepte-kinder', title: 'Eid Rezepte für Kinder', readTime: '8 Min.' },
    ],
    icon: Heart,
  },
  {
    title: 'Ramadan Wissen',
    desc: 'Grundlagen, Spiritualität und Tipps für den Fastenmonat.',
    articles: [
      { slug: 'ramadan-fuer-anfaenger', title: 'Ramadan für Anfänger: Der komplette Guide', readTime: '12 Min.' },
      { slug: 'zakat-sadaqah-eid', title: 'Zakat und Sadaqah: Geben im Islam', readTime: '10 Min.' },
      { slug: 'was-ist-eid', title: 'Was ist Eid? Ein Leitfaden', readTime: '9 Min.' },
    ],
    icon: BookOpen,
  },
];

export default function RamadanHubPage() {
  return (
    <main className="pt-20 md:pt-24 bg-[#FDFCF8]" data-testid="ramadan-hub-page">
      <SEOHead
        title="Ramadan 2026: Geschenke, Rezepte, Deko & Tipps"
        description="Dein kompletter Ramadan Guide 2026: Geschenkideen, Iftar-Rezepte, Deko-Inspiration, Aktivitäten für Kinder und spirituelle Tipps. Alles für einen gesegneten Ramadan."
        canonical="/ramadan"
      />

      {/* Hero */}
      <section className="py-16 md:py-24 bg-[#4A5D4F] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-20 w-40 h-40 border-2 border-[#D4C5A9] rounded-full" />
          <div className="absolute bottom-10 left-10 w-60 h-60 border-2 border-[#D4C5A9] rounded-full" />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 text-[#D4C5A9] mb-6">
              <Lamp className="w-5 h-5" />
              <span className="text-sm font-medium tracking-widest uppercase">Ramadan 2026</span>
            </div>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-medium text-white mb-6 leading-tight">
              Ramadan Guide 2026
            </h1>
            <p className="text-lg text-white/80 leading-relaxed mb-8">
              Alles was du für einen gesegneten Ramadan brauchst: Geschenkideen, Iftar-Rezepte,
              Deko-Inspiration, Aktivitäten für Kinder und spirituelle Tipps – an einem Ort.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/geschenke" className="inline-flex items-center gap-2 bg-white text-[#4A5D4F] px-6 py-3 rounded-full font-medium hover:bg-[#D4C5A9] transition-colors" data-testid="ramadan-cta-gifts">
                Geschenke entdecken <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/ratgeber" className="inline-flex items-center gap-2 bg-white/10 text-white px-6 py-3 rounded-full font-medium hover:bg-white/20 transition-colors" data-testid="ramadan-cta-guides">
                Alle Ratgeber
              </Link>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 md:px-12 mt-4">
        <Breadcrumbs items={[{ label: 'Ramadan', path: '/ramadan' }]} />
      </div>

      {/* Sections */}
      {sections.map((section) => (
        <section key={section.title} className="py-12 md:py-16 border-b border-[#E5E7EB] last:border-0">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-[#4A5D4F]/10 flex items-center justify-center">
                <section.icon className="w-5 h-5 text-[#4A5D4F]" />
              </div>
              <div>
                <h2 className="font-serif text-xl md:text-2xl font-medium text-[#2C332E]">{section.title}</h2>
                <p className="text-sm text-[#6B7280]">{section.desc}</p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {section.articles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/ratgeber/${article.slug}`}
                  className="group flex items-center justify-between p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-all"
                  data-testid={`ramadan-article-${article.slug}`}
                >
                  <div className="min-w-0">
                    <h3 className="text-sm font-medium text-[#2C332E] group-hover:text-[#4A5D4F] transition-colors truncate">{article.title}</h3>
                    <span className="flex items-center gap-1 text-xs text-[#6B7280] mt-1"><Calendar className="w-3 h-3" />{article.readTime}</span>
                  </div>
                  <ArrowRight className="w-4 h-4 text-[#4A5D4F] opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 ml-2" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* CTA */}
      <section className="py-16 md:py-20 bg-[#F5F5F0]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E] mb-4">Bereit für Eid?</h2>
          <p className="text-[#6B7280] mb-8">Entdecke jetzt handverlesene Geschenke für deine Liebsten.</p>
          <Link to="/geschenke" className="inline-flex items-center gap-2 bg-[#4A5D4F] text-white px-8 py-3 rounded-full font-medium hover:bg-[#3A493E] transition-colors">
            Geschenke entdecken <Gift className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </main>
  );
}
